/**
 * Queue lanes, SLA clocks, the BD gate, batch approval and the daily digest
 * — chunk 10, `server/app/routers/queue.py` (`server/app/services/queue.py`
 * for what each rule actually checks).
 *
 * Two rules this module follows, both inherited from the server rather than
 * decided here:
 *
 * - **`lane` is the server's word.** `Lane.READY` requires a clean run, a
 *   verified deadline, a cleared BD gate and no existing approval — this
 *   module never recomputes that from the four booleans a row also carries;
 *   the second gate is always the lenient one (I4's rule, applied to a lane
 *   assignment the same way it applies to `exportable`).
 * - **Batch approval reports per-matter, always 200.** A batch that
 *   partially succeeds is still a successful call — `skipped` names why
 *   each matter that didn't make it didn't, never an HTTP error for the
 *   whole batch over one bad id.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'

import { api } from './client'
import { pipelineKeys } from './pipeline'
import type {
  BatchApproveResult,
  BdGateClearResult,
  Digest,
  QueueResponse,
} from './types'

export const queueKeys = {
  queue: (today: string | null) => ['queue', today ?? ''] as const,
  digest: (date: string | null) => ['queue', 'digest', date ?? ''] as const,
}

/**
 * The live queue — every non-terminal matter, lane-assigned and SLA-clocked.
 *
 * `today` is an override for tests/reproducibility only; a real caller omits
 * it and gets the server's own clock. Passing it does not make this a
 * `useQuery` a stray render could cache staleness into — the server always
 * answers fresh, this is a live operational view, not the digest's snapshot.
 */
export function useQueue(today?: string): UseQueryResult<QueueResponse> {
  return useQuery<QueueResponse>({
    queryKey: queueKeys.queue(today ?? null),
    queryFn: () => api.get<QueueResponse>('/api/queue', { query: { today } }),
  })
}

/**
 * The daily digest — a point-in-time snapshot as of `date` (server's own
 * today if omitted), not an activity log of what happened that day. Two
 * calls with the same `date` against unchanged data return byte-identical
 * counts (the server's own acceptance test for this feature).
 */
export function useDigest(date?: string): UseQueryResult<Digest> {
  return useQuery<Digest>({
    queryKey: queueKeys.digest(date ?? null),
    queryFn: () => api.get<Digest>('/api/queue/digest', { query: { date } }),
  })
}

/**
 * Approve every matter id in one call. Human-only server-side — this hook
 * cannot make an API-key session pass that gate, only surface the 403
 * `human_required` the server already answers with.
 *
 * Invalidates the queue and every matter's own run list touched, so the
 * lane a just-approved matter shows next reflects the approval without a
 * manual refetch.
 */
export function useBatchApprove() {
  const queryClient = useQueryClient()
  return useMutation<BatchApproveResult, unknown, string[]>({
    mutationFn: (matterIds) =>
      api.post<BatchApproveResult>('/api/queue/batch-approve', { matter_ids: matterIds }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['queue'] })
    },
  })
}

/**
 * Clear a matter's BD gate (REQ-8). Role-gated server-side to `bd`/`admin`
 * — an analyst session gets a 403 this hook only surfaces, never bypasses.
 */
export function useClearBdGate() {
  const queryClient = useQueryClient()
  return useMutation<BdGateClearResult, unknown, string>({
    mutationFn: (matterId) =>
      api.post<BdGateClearResult>(
        `/api/matters/${encodeURIComponent(matterId)}/bd-gate/clear`,
      ),
    onSuccess: (_result, matterId) => {
      void queryClient.invalidateQueries({ queryKey: ['queue'] })
      void queryClient.invalidateQueries({ queryKey: pipelineKeys.matter(matterId) })
    },
  })
}
